import { COMMAND_META } from '@glyph/protocol-ts';
import type { CmdResult, CommandAction, CommandPayload, CommandResult, DeviceId, Topic } from '@glyph/protocol-ts';
import { createRest } from './rest';
import { MuxSocket } from './ws';
import { AudioSessionImpl } from './audioSession';
import type { AudioSession, ClientEvent, CmdOptions, CommandClient, EventHandler, RestClient, Unsubscribe } from './types';
import { CommandError, unwrapCmdResult } from './types';
import { gatewayFrameToEvents } from '../adapters/cloud';

export interface CloudClientOptions {
  origin?: string;
  /** Override for the CSRF header value; by default the token the API returns with `me` / `login` is used. */
  csrf?: () => string | null;
}

/** `CmdOut` (cloud/glyph_cloud/api/schemas.py): the command row, 202 while queued for an offline device. */
interface CloudCmdResponse {
  id: string;
  status: 'ok' | 'error' | 'queued' | 'sent' | 'timeout' | 'expired' | 'cancelled' | string;
  result?: CmdResult | null;
  error?: string | null;
}

/**
 * Fleet console transport:
 *   POST /api/devices/{id}/cmd  {action, payload, wait, timeout_s}  -> CmdOut {id, status, result?, error?}
 *   GET  /api/...                                                  -> REST (session cookie + X-CSRF-Token)
 *   WS   /gw/browser  {op:'sub'|'unsub'|'listen'|'unlisten'|'ping', device_id, topics?, ticket?}
 *        <- {device_id, t, ts, d}; audio as [16-byte device uuid][0x01][seq][ts][opus]
 */
export class CloudClient implements CommandClient {
  readonly kind = 'cloud' as const;
  readonly selfDeviceId = null;
  readonly rest: RestClient;
  private sock: MuxSocket;
  private csrfToken: string | null = null;

  constructor(o: CloudClientOptions = {}) {
    const origin = o.origin ?? (typeof location !== 'undefined' ? location.origin : 'http://localhost:8000');
    this.rest = createRest({
      base: `${origin}/api`,
      headers: (): Record<string, string> => {
        const c = o.csrf?.() ?? this.csrfToken;
        return c ? { 'X-CSRF-Token': c } : {};
      },
    });
    this.sock = new MuxSocket({
      url: () => {
        const u = new URL('/gw/browser', origin);
        u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
        return u.toString();
      },
      subFrame: (device_id, topics) => ({ op: 'sub', device_id, topics: this.allTopics(device_id, topics) }),
      // The gateway subscribes per device, not per topic: re-send the remaining set, or drop the device.
      unsubFrame: (device_id) => {
        const left = this.sock.topicsFor(device_id);
        return left.length ? { op: 'sub', device_id, topics: left } : { op: 'unsub', device_id };
      },
      parse: (msg): ClientEvent[] => gatewayFrameToEvents(msg),
      pingFrame: { op: 'ping' },
      pingIntervalMs: 20000,
      binaryPrefixBytes: 16,
    });
  }

  /** Every topic subscribed for the device (the fresh ones plus what is already refcounted). */
  private allTopics(deviceId: DeviceId, fresh: readonly Topic[]): string[] {
    return [...new Set<string>([...this.sock.topicsFor(deviceId), ...fresh])].filter((t) => t !== 'audio_opus');
  }

  setCsrf(token: string | null) {
    this.csrfToken = token;
  }

  async cmd<A extends CommandAction>(deviceId: DeviceId, action: A, payload: CommandPayload<A>, opts?: CmdOptions): Promise<CommandResult<A>> {
    if (!deviceId) throw new CommandError('no device selected', 'rejected', 404); // never POST /devices//cmd
    const meta = COMMAND_META[action];
    const timeoutMs = opts?.timeoutMs ?? meta.timeoutMs;
    const ctl = new AbortController();
    const to = setTimeout(() => ctl.abort(), timeoutMs + 2000);
    opts?.signal?.addEventListener('abort', () => ctl.abort());
    try {
      const r = await this.rest.post<CloudCmdResponse>(
        `/devices/${encodeURIComponent(deviceId)}/cmd`,
        { action, payload: payload ?? {}, wait: true, timeout_s: Math.max(1, Math.min(600, timeoutMs / 1000)), confirm: true },
        { signal: ctl.signal },
      );
      if (r.status === 'ok' && r.result) {
        // The gateway stores the device's CmdResult.data flattened (cloud/glyph_cloud/core/commands.py
        // apply_result); a few documents (status.get) carry their own `ok` field, so only unwrap a real
        // {ok, data} envelope.
        const res = r.result as unknown as Record<string, unknown>;
        const enveloped = typeof res.ok === 'boolean' && ('data' in res || 'error' in res);
        return enveloped ? unwrapCmdResult<CommandResult<A>>(action, r.result) : (res as unknown as CommandResult<A>);
      }
      if (r.status === 'queued' || r.status === 'sent') return { ok: true, queued: true, cmd_id: r.id } as unknown as CommandResult<A>;
      if (r.status === 'timeout') throw new CommandError(`${action} timed out on device`, 'timeout', 504);
      if (r.status === 'expired' || r.status === 'cancelled') throw new CommandError(`${action} ${r.status}`, 'offline', 409);
      throw new CommandError(r.error ?? `${action} failed`, 'rejected', undefined, r.result ?? undefined);
    } catch (e) {
      if (ctl.signal.aborted) throw new CommandError(`${action} timed out`, 'timeout');
      throw e;
    } finally {
      clearTimeout(to);
    }
  }

  subscribe(deviceId: DeviceId, topics: readonly Topic[], onEvent: EventHandler): Unsubscribe {
    return this.sock.subscribe(deviceId, topics, onEvent);
  }

  listen(deviceId: DeviceId): AudioSession {
    // POST /listen mints a 60 s ticket; the socket op makes this connection a listener. The gateway
    // starts the device's Opus monitor for the first listener and stops it after the last one.
    return new AudioSessionImpl(this, deviceId, {
      start: async () => {
        const t = await this.rest.post<{ ticket: string }>(`/devices/${encodeURIComponent(deviceId)}/listen`);
        this.sock.send({ op: 'listen', device_id: deviceId, ticket: t.ticket });
      },
      stop: async () => {
        this.sock.send({ op: 'unlisten', device_id: deviceId });
      },
    });
  }

  previewUrl(): string | null {
    return null; // the cloud relays captions, not frames; screens render the text preview
  }

  reconnect() {
    this.sock.reconnect();
  }

  close() {
    this.sock.close();
  }
}
